import type { Biome, Tile, TileType } from '../../core/tile';
import {
  BIOME_ANCHOR,
  type ColourHex,
  DANGER_TINT,
  FRONTIER_GLYPH,
  FRONTIER_SLATE,
  HEX_OUTLINE_COLOUR,
  HEX_OUTLINE_HOVER_COLOUR,
  LANDMARK_HAZE,
  MEMORY_BLEND_TARGET,
  TILE_GLYPH_FILL,
  TILE_GLYPH_STROKE,
  WORLD_TYPE_COLOUR,
} from '../../theme';

/**
 * Render face — what visual mode a hex is drawn in.
 *
 * Post ADR-0014 the four-tier fog (T1/T2/T3/T4) is replaced by these modes:
 *   - `natural`         — globally discovered AND in the current character's vision.
 *   - `memory`          — real face known (discovered / always-visible terrain) AND
 *                         out of the current character's vision. Desaturated
 *                         derivative of the natural face.
 *   - `frontier`        — globally undiscovered AND in vision. The `?` card-back
 *                         face; biome tint composes through.
 *   - `memory-frontier` — seen at least once but still undiscovered AND out of
 *                         vision (HEX-128). A desaturated `?` card-back: remembered
 *                         geography, content still hidden (HEX-85). Stops glimpsed
 *                         tiles reverting to black once they leave the vision disk.
 *   - `landmark`        — distant always-visible terrain (mountains, HEX-127) beyond
 *                         the vision/memory radius but within the skyline scan. A cool
 *                         hazed silhouette — orientation only, never a content tile.
 *
 * Tiles never seen AND out of vision are not rendered (ADR-0014 §1), the lone
 * exception being landmark mountains (HEX-127).
 */
export type RenderFace =
  | 'natural'
  | 'footprint'
  | 'memory'
  | 'frontier'
  | 'memory-frontier'
  | 'landmark';

/** RGB integer colour as Pixi expects (`0xRRGGBB`). */
export type { ColourHex };

/**
 * Colour constants now live in the Moss & Bone theme (ADR-0020 §2, HEX-100);
 * `faceColour` carries no bare hex. Re-exported here so existing render-path
 * consumers keep importing them from this module.
 */
export {
  DANGER_TINT,
  FRONTIER_GLYPH,
  FRONTIER_SLATE,
  HEX_OUTLINE_COLOUR,
  HEX_OUTLINE_HOVER_COLOUR,
  LANDMARK_HAZE,
  MEMORY_BLEND_TARGET,
  TILE_GLYPH_FILL,
  TILE_GLYPH_STROKE,
};

/**
 * Per-type colour palette. Pillars get a distinct landmark colour so they
 * stand out at a glance — ADR-0004 anchors them as the bootstrap fast-travel
 * cue. `road`, `shop`, `town` placeholders pending a renderer ticket that
 * locks the spawn-town cluster visual identity (ADR-0012 §6 / §9).
 *
 * `mountain` is the impassable-terrain face; biome-impassable lake/mountain
 * cells overpaint this with the solid biome tint (`biomeBlendedColour`).
 *
 * `dangerous` shares `ground`'s face (HEX-85): a trap must be indistinguishable
 * from plain ground until sprung, so it carries no distinct colour, glyph, or
 * landmark/always-visible treatment — it walks the whole face path as ground.
 *
 * Values live in the theme (`WORLD_TYPE_COLOUR`) — ADR-0020 §5 hands any re-pick
 * of these tile colours to the ADR-0018 World View wave.
 */
const TYPE_COLOUR: Readonly<Record<TileType, ColourHex>> = WORLD_TYPE_COLOUR;

/** Linear blend fraction toward `MEMORY_BLEND_TARGET` for memory mode (ADR-0014 §1). */
const MEMORY_BLEND_T = 0.55;
/** Chroma multiplier applied after the memory blend — drops saturation by 60%. */
const MEMORY_CHROMA = 0.4;

/**
 * Footprint-mode constants — globally discovered tiles in vision that THIS
 * character hasn't personally crossed. Half-strength of memory mode: visibly
 * inherited but still clearly "in vision", so YOUR explored tiles read as
 * brighter alongside.
 */
const FOOTPRINT_BLEND_T = 0.25;
const FOOTPRINT_CHROMA = 0.7;

/**
 * Landmark-mode constants (HEX-127) — distant mountains surfaced as orientation
 * silhouettes beyond the active-vision / memory radius. A stronger blend toward
 * the cool `LANDMARK_HAZE` than memory uses, with most (not all) chroma kept,
 * reads as a hazy blue ridge on the skyline — visibly distinct from the neutral
 * mid-tone of a memory tile.
 */
const LANDMARK_BLEND_T = 0.7;
const LANDMARK_CHROMA = 0.3;

/**
 * Legacy alias — HEX-42 callers reference `UNDISCOVERED_PLACEHOLDER`. The
 * concept is the same as the post-ADR-0014 frontier slate face.
 */
export const UNDISCOVERED_PLACEHOLDER: ColourHex = FRONTIER_SLATE;

const DANGER_BLEND_T = 0.5;

/** Blend a base face colour toward the sprung-trap tint (HEX-96). */
export function dangerBlendedColour(base: ColourHex): ColourHex {
  return blendColour(base, DANGER_TINT, DANGER_BLEND_T);
}

/**
 * Biome tint palette (HEX-31, ADR-0013 §10) — the canonical Moss & Bone biome
 * anchors (ADR-0020 §2). `lake` and `mountain` are impassable biomes carrying no
 * discoverables (ADR-0013 §4) — their tint paints a solid biome face that
 * overrides the per-type face. `forest` and `snow` blend at a fixed strength.
 * `plain` uses a blend factor of 0 so the per-type face passes through untouched
 * (its anchor value is therefore inert in `biomeBlendedColour`).
 */
export const BIOME_TINT: Readonly<Record<Biome, ColourHex>> = BIOME_ANCHOR;

type BiomeCompose = { kind: 'solid' } | { kind: 'blend'; t: number };

const BIOME_COMPOSE: Readonly<Record<Biome, BiomeCompose>> = {
  plain: { kind: 'blend', t: 0 },
  forest: { kind: 'blend', t: 0.45 },
  lake: { kind: 'solid' },
  snow: { kind: 'blend', t: 0.55 },
  mountain: { kind: 'solid' },
};

/**
 * Landmark / discoverable types blend with the biome tint at this fraction
 * of the terrain blend — keeps each face dominant inside its biome.
 */
const LANDMARK_BLEND_SCALE = 0.35;

function isLandmarkType(type: TileType): boolean {
  switch (type) {
    case 'ground':
    case 'dangerous': // HEX-85: hidden trap blends exactly as ground does.
    case 'mountain':
      return false;
    case 'pillar':
    case 'road':
    case 'shop':
    case 'town':
    case 'encounter':
    case 'chest':
    case 'traveller-merchant':
      return true;
  }
}

/** True when the tile has been first-crossed by any character. */
export function isDiscovered(tile: Tile): boolean {
  return tile.metadata.discoveredBy !== null;
}

/**
 * Topography / landmarks that render their real face whenever the tile is in
 * vision, even before discovery. These types are the world's navigation
 * primitives — mountains form corridors, pillars / roads / shops / towns are
 * settlement anchors. The `?` discovery mechanic applies to *content* tiles
 * (ground / dangerous / encounter / chest / traveller-merchant) whose identity
 * is the discovery itself — dangerous joins this set per HEX-85 so a trap stays
 * a face-down `?` until crossed, indistinguishable from the ground around it.
 *
 * (Amendment to HEX-42 — the original ticket put every type behind `?`, which
 * broke topology-as-navigation. Fixed 2026-05-27 after the smoke surfaced
 * mountains rendering as `?` cardbacks.)
 */
export function isAlwaysVisibleTerrain(type: TileType): boolean {
  switch (type) {
    case 'mountain':
    case 'pillar':
    case 'road':
    case 'shop':
    case 'town':
      return true;
    case 'ground':
    case 'dangerous': // HEX-85: hidden trap — `?` until crossed, like ground.
    case 'encounter':
    case 'chest':
    case 'traveller-merchant':
      return false;
  }
}

/** True when a tile renders its real type face — discovered, OR always-visible terrain. */
export function showsRealFace(tile: Tile): boolean {
  return isDiscovered(tile) || isAlwaysVisibleTerrain(tile.type);
}

/**
 * Base per-tile colour — the frontier slate face for tiles whose identity is
 * still hidden (`!showsRealFace`), otherwise the per-type face. Biome
 * composition happens on top.
 */
function tileBaseColour(tile: Tile): ColourHex {
  if (!showsRealFace(tile)) return FRONTIER_SLATE;
  return TYPE_COLOUR[tile.type];
}

function blendColour(a: ColourHex, b: ColourHex, t: number): ColourHex {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

function clamp255(value: number): number {
  if (value < 0) return 0;
  if (value > 255) return 255;
  return Math.round(value);
}

/**
 * Drop chroma toward luminance by `factor` (0 = full grey, 1 = unchanged).
 * Operates in sRGB — close enough for the subtle desaturation memory mode
 * needs without an sRGB→linear round-trip.
 */
function desaturate(colour: ColourHex, factor: number): ColourHex {
  const r = (colour >> 16) & 0xff;
  const g = (colour >> 8) & 0xff;
  const b = colour & 0xff;
  const luma = 0.299 * r + 0.587 * g + 0.114 * b;
  const nr = clamp255(luma + (r - luma) * factor);
  const ng = clamp255(luma + (g - luma) * factor);
  const nb = clamp255(luma + (b - luma) * factor);
  return (nr << 16) | (ng << 8) | nb;
}

/**
 * Compose the per-tile face with the biome tint. Pure helper exported for
 * the map overlay so the schematic view stays in sync with the world canvas.
 *
 * Lake and mountain biomes return the solid biome tint regardless of tile —
 * impassable biomes wash everything below them. Other biomes return the
 * tile-base colour blended toward the biome tint; the blend is reduced for
 * landmark/discoverable types so each face stays legible inside its biome.
 *
 * Per HEX-42: undiscovered tiles render the placeholder *under* the biome
 * tint, so a `?` in a forest still reads as forest-flavoured.
 */
export function biomeBlendedColour(tile: Tile): ColourHex {
  const compose = BIOME_COMPOSE[tile.biome];
  if (compose.kind === 'solid') {
    return BIOME_TINT[tile.biome];
  }
  const base = tileBaseColour(tile);
  if (compose.t === 0) return base;
  // Landmark blend scaling applies whenever the tile shows its real face
  // (discovered, or always-visible terrain). While it is still a `?` placeholder
  // it uses the full blend so the placeholder picks up biome flavour the same
  // way a ground cell would.
  const blendFraction =
    showsRealFace(tile) && isLandmarkType(tile.type) ? compose.t * LANDMARK_BLEND_SCALE : compose.t;
  return blendColour(base, BIOME_TINT[tile.biome], blendFraction);
}

/**
 * Memory-mode composition — desaturated derivative of the biome-blended
 * natural face. Pure helper so the map overlay (HEX-45 §B) picks the same
 * "world memory" palette.
 */
export function memoryBlendedColour(tile: Tile): ColourHex {
  const natural = biomeBlendedColour(tile);
  const dimmed = blendColour(natural, MEMORY_BLEND_TARGET, MEMORY_BLEND_T);
  return desaturate(dimmed, MEMORY_CHROMA);
}

/**
 * A frontier shadow of `tile` — an undiscovered plain `ground` tile sharing its
 * biome — so face composition falls through to `FRONTIER_SLATE` regardless of the
 * real type/discovery state. Keeps the `?` card-back's content hidden (HEX-85)
 * while letting the biome tint compose through.
 */
function frontierShadow(tile: Tile): Tile {
  return {
    ...tile,
    type: 'ground',
    metadata: { ...tile.metadata, discoveredBy: null },
  };
}

/**
 * Memory-frontier mode (HEX-128) — a tile seen at least once but still
 * undiscovered, now out of vision. The memory-dimmed slate `?` card-back:
 * remembered geography that stops reverting to black, with content still hidden
 * until crossed (HEX-85). Derived by memory-blending the frontier shadow, so it
 * sits in the same desaturated palette as `memory` but reads as a `?`, never the
 * real content face.
 */
export function memoryFrontierColour(tile: Tile): ColourHex {
  return memoryBlendedColour(frontierShadow(tile));
}

/**
 * Footprint mode — globally discovered tile in current vision that THIS
 * character has NOT personally crossed. Half-strength of memory mode: the
 * tile reads as "in vision but inherited knowledge", so personally-explored
 * tiles next to it read as brighter ("YOUR map") by contrast.
 */
export function footprintBlendedColour(tile: Tile): ColourHex {
  const natural = biomeBlendedColour(tile);
  const dimmed = blendColour(natural, MEMORY_BLEND_TARGET, FOOTPRINT_BLEND_T);
  return desaturate(dimmed, FOOTPRINT_CHROMA);
}

/**
 * Landmark mode (HEX-127) — distant mountain silhouette. Blends the biome-blended
 * natural face toward the cool `LANDMARK_HAZE` and keeps a sliver of chroma, so a
 * far peak reads as a hazy blue ridge you can steer by rather than a remembered
 * (neutral) tile. Pure helper, tested for distinctness from `memoryBlendedColour`.
 */
export function landmarkBlendedColour(tile: Tile): ColourHex {
  const natural = biomeBlendedColour(tile);
  const hazed = blendColour(natural, LANDMARK_HAZE, LANDMARK_BLEND_T);
  return desaturate(hazed, LANDMARK_CHROMA);
}

/**
 * Pick the fill colour for a rendered hex.
 *
 * `natural`         → biome-blended per-type face.
 * `memory`          → memory-blended derivative of natural.
 * `frontier`        → biome-blended slate cardback (treats the tile as
 *                     undiscovered for face composition; biome tint composes on
 *                     top so a `?` in a forest still reads as forest-flavoured).
 * `memory-frontier` → memory-dimmed slate cardback (a remembered `?`, HEX-128).
 */
export function faceColour(face: RenderFace, tile: Tile): ColourHex {
  if (face === 'memory') return memoryBlendedColour(tile);
  if (face === 'memory-frontier') return memoryFrontierColour(tile);
  if (face === 'footprint') return footprintBlendedColour(tile);
  if (face === 'landmark') return landmarkBlendedColour(tile);
  if (face === 'frontier') {
    // Force-render the slate cardback even if the tile is technically
    // discovered or terrain-visible — the renderer only assigns 'frontier' to
    // tiles it wants to hide. Use a shadow tile so tileBaseColour falls
    // through to FRONTIER_SLATE.
    return biomeBlendedColour(frontierShadow(tile));
  }
  return biomeBlendedColour(tile);
}
